import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useAppConfig } from '../api/cache/appConfigQueryOptions.js';
import { getBrandLogo } from '../utils/assetUtils.js';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import { ConciergeChatAreaV2 } from './components/ConciergeChatAreaV2.js';
import { ConciergeHeader } from './components/ConciergeHeader/index.js';
import { ConciergeSidebar } from './components/ConciergeSidebar.js';
import { useAutoScroll } from './hooks/useAutoScroll.js';
import { useAutoSendFromUrl } from './hooks/useAutoSendFromUrl.js';
import { useConversationLoader } from './hooks/useConversationLoader.js';
import { useConciergeContext } from './ConciergeContextProvider.js';
import DevOptionsPanel from './components/DevOptionsPanel.js';
import { useDevOptions } from './hooks/useDevOptions.js';
import { useStreamingSearch } from './hooks/useStreamingSearch.js';
import { useUrlSync } from './hooks/useUrlSync.js';
import { Drawer } from '@mui/material';
import { BRANDCLUB_BRAND_ID, BRANDCLUB_BRAND_NAME } from '../utils/constants/Constants.js';

const ConciergeContainer = ({ layout = 'header', isEmbedded, cartCount = 0, onBagClick, isCheckoutDrawerOpen = false, CheckoutDrawerContent, onVariantSelect, userProfile, onSignOutClick, onHomeClick, userFirstName, }) => {
    // Default isEmbedded based on layout: true for sidebar (landing-page), false for header (mobile)
    const effectiveIsEmbedded = isEmbedded !== null && isEmbedded !== void 0 ? isEmbedded : layout === 'sidebar';
    // Conversation state (centralized via context - includes apiBaseUrl)
    const conversationState = useConciergeContext();
    const [appLogoUrl, setAppLogoUrl] = useState(undefined);
    const [appName, setAppName] = useState(undefined);
    const [brandId, setBrandId] = useState(undefined);
    // Brand config
    const params = new URLSearchParams(window.location.search);
    const appConfigId = params.get('appConfigId') || null;
    const { data: appConfig } = useAppConfig();
    useEffect(() => {
        var _a, _b;
        if (appConfig) {
            const brandId = ((_a = appConfig === null || appConfig === void 0 ? void 0 : appConfig.domainConfig) === null || _a === void 0 ? void 0 : _a.brandId) || BRANDCLUB_BRAND_ID;
            const appLogoUrl = getBrandLogo(brandId);
            const appName = ((_b = appConfig === null || appConfig === void 0 ? void 0 : appConfig.domainConfig) === null || _b === void 0 ? void 0 : _b.brandName) || BRANDCLUB_BRAND_NAME;
            setAppLogoUrl(appLogoUrl);
            setAppName(appName);
            setBrandId(brandId);
        }
    }, [appConfig]);
    // Sidebar expanded state (for sidebar layout only - so top bar can show logo when collapsed)
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
    // Input state
    const [searchInputValue, setSearchInputValue] = useState('');
    // Dev options - enabled via ?isDev=true URL param
    const devOpts = useDevOptions();
    // Auto-scroll behavior
    const { chatWrapperRef: _chatWrapperRef, messagesEndRef: _messagesEndRef, shouldScrollOnNextMessageRef, scrollToBottom, } = useAutoScroll(conversationState.conversationHistory.length, conversationState.isActivelyStreaming, conversationState.streamingAIResponse, conversationState.conversationHistory.length === 0);
    // Handle stream errors - restore input and show appropriate toast
    const handleStreamError = (errorType, originalMessage) => {
        setSearchInputValue(originalMessage);
        const errorMessages = {
            rate_limit: 'Too many requests. Please wait a moment before trying again.',
            network: 'Network error. Please check your connection and try again.',
            server: 'Something went wrong on our end. Please try again.',
            unknown: 'Something went wrong. Please try again.',
        };
        toast.error(errorMessages[errorType]);
    };
    // Streaming search
    const { performSearch, cancelStream } = useStreamingSearch({
        brandId,
        appConfigId,
        conversationState,
        scrollToBottom,
        shouldScrollOnNextMessageRef,
        onStreamError: handleStreamError,
        devOptions: devOpts.devOptions,
    });
    // Refresh the sidebar conversation history list (immediately + 5s delayed)
    const queryClient = useQueryClient();
    const refreshConversationHistory = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }, 5000);
    }, [queryClient]);
    // Handle search submission
    const handleSearch = (searchTerm, imageData, imageMimeType) => __awaiter(void 0, void 0, void 0, function* () {
        setSearchInputValue('');
        refreshConversationHistory();
        yield performSearch(searchTerm, imageData, imageMimeType);
    });
    // Handle update feedback
    const handleUpdateFeedback = (messageIndex, feedback) => {
        // Update the feedback in the conversation history
        conversationState.setConversationHistory((prev) => {
            return prev.map((msg, idx) => {
                if (idx === messageIndex) {
                    return Object.assign(Object.assign({}, msg), { feedback: feedback || undefined });
                }
                return msg;
            });
        });
    };
    // Handle conversation selection from history
    const handleConversationSelect = (conversationId) => {
        // Cancel any in-flight stream before switching conversations
        cancelStream();
        conversationState.setIsActivelyStreaming(false);
        conversationState.clearStreamingState();
        // If we're already viewing this conversation, just reload it by updating the ID
        // This ensures a fresh load without the jarring clear + reload
        if (conversationState.conversationId === conversationId) {
            // Force reload by temporarily clearing and resetting the ID
            // This will trigger useConversationLoader to reload
            conversationState.setConversationId(null);
            // Use setTimeout to ensure the state update is processed
            setTimeout(() => {
                conversationState.setConversationId(conversationId);
            }, 0);
            return;
        }
        // Clear current conversation state before loading new one
        conversationState.setConversationHistory([]);
        conversationState.setProductMetadataMap(new Map());
        conversationState.setStreamingProductGroups(new Map());
        conversationState.setPreviewSearchQueries([]);
        conversationState.setPreviewQueryProducts([]);
        conversationState.setIsWaitingForFollowUpQueries(false);
        conversationState.setStreamingAIResponse('');
        conversationState.setCurrentStatus('');
        conversationState.setFollowUpQuestions([]);
        // Clear sharing state so old dividers don't flash while new conversation loads
        conversationState.setIsSharedView(false);
        conversationState.setSharedByUserName(undefined);
        conversationState.setSharedAtMessageIndex(undefined);
        conversationState.setSourceConversationId(undefined);
        conversationState.setSourceCheckpointIndex(undefined);
        // Update URL with the selected conversation ID
        const params = new URLSearchParams(window.location.search);
        params.set('conversationId', conversationId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({}, '', newUrl);
        // Update conversation state
        conversationState.setConversationId(conversationId);
    };
    // Handle compose button click (go back to welcome screen)
    const handleComposeClick = () => {
        // Cancel any in-flight stream before clearing state
        cancelStream();
        conversationState.setIsActivelyStreaming(false);
        conversationState.clearStreamingState();
        // Clear conversation state
        conversationState.setConversationHistory([]);
        conversationState.setProductMetadataMap(new Map());
        conversationState.setStreamingProductGroups(new Map());
        conversationState.setPreviewSearchQueries([]);
        conversationState.setPreviewQueryProducts([]);
        conversationState.setIsWaitingForFollowUpQueries(false);
        conversationState.setStreamingAIResponse('');
        conversationState.setCurrentStatus('');
        conversationState.setFollowUpQuestions([]);
        conversationState.setConversationId(null);
        // Clear sharing state so dividers don't persist on the welcome screen
        conversationState.setIsSharedView(false);
        conversationState.setSharedByUserName(undefined);
        conversationState.setSharedAtMessageIndex(undefined);
        conversationState.setSourceConversationId(undefined);
        conversationState.setSourceCheckpointIndex(undefined);
        // Remove conversationId from URL
        const params = new URLSearchParams(window.location.search);
        params.delete('conversationId');
        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
        window.history.pushState({}, '', newUrl);
    };
    // Conversation loader
    const { isLoadingConversationQuery, isPendingAssistantResponse, isPollingTimedOut, resetPendingState } = useConversationLoader({
        conversationId: conversationState.conversationId,
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationHistoryLength: conversationState.conversationHistory.length,
        apiBaseUrl: conversationState.apiBaseUrl,
        onConversationLoaded: (messages, productMap, surveyMap, followUpQuestions, previewSearchQueries, previewQueryProducts, sharingInfo) => {
            var _a;
            conversationState.setProductMetadataMap(productMap);
            conversationState.setSurveyMetadataMap(surveyMap);
            conversationState.setConversationHistory(messages);
            conversationState.setFollowUpQuestions(followUpQuestions);
            conversationState.setPreviewSearchQueries(previewSearchQueries);
            conversationState.setPreviewQueryProducts(previewQueryProducts);
            // Propagate sharing metadata into context state
            if (sharingInfo) {
                conversationState.setIsSharedView((_a = sharingInfo.isSharedView) !== null && _a !== void 0 ? _a : false);
                conversationState.setSharedByUserName(sharingInfo.sharedByUserName);
                conversationState.setSharedAtMessageIndex(sharingInfo.sharedAtMessageIndex);
                conversationState.setSourceConversationId(sharingInfo.sourceConversationId);
                conversationState.setSourceCheckpointIndex(sharingInfo.sourceCheckpointIndex);
            }
            else {
                conversationState.setIsSharedView(false);
                conversationState.setSharedByUserName(undefined);
                conversationState.setSharedAtMessageIndex(undefined);
                conversationState.setSourceConversationId(undefined);
                conversationState.setSourceCheckpointIndex(undefined);
            }
        },
        onConversationNotFound: () => {
            const params = new URLSearchParams(window.location.search);
            params.delete('conversationId');
            const newUrl = params.toString()
                ? `${window.location.pathname}?${params.toString()}`
                : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            conversationState.setConversationId(null);
        },
    });
    // Handle retry when polling for assistant response times out
    const handleRetry = () => {
        resetPendingState();
        const history = conversationState.conversationHistory;
        if (history.length === 0)
            return;
        // The last message should be the user message that never got a response
        const lastMsg = history[history.length - 1];
        if (lastMsg.role !== 'user')
            return;
        // Remove it from history (handleSearch → performSearch will re-add it)
        conversationState.setConversationHistory(history.slice(0, -1));
        // Re-send via streaming
        handleSearch(lastMsg.content);
    };
    // Refresh sidebar when streaming ends (new message completed)
    const wasStreamingRef = useRef(false);
    useEffect(() => {
        if (wasStreamingRef.current && !conversationState.isActivelyStreaming) {
            refreshConversationHistory();
        }
        wasStreamingRef.current = conversationState.isActivelyStreaming;
    }, [conversationState.isActivelyStreaming, refreshConversationHistory]);
    // Refresh sidebar when polling for pending assistant response ends
    const wasPendingRef = useRef(false);
    useEffect(() => {
        if (wasPendingRef.current && !isPendingAssistantResponse) {
            refreshConversationHistory();
        }
        wasPendingRef.current = isPendingAssistantResponse;
    }, [isPendingAssistantResponse, refreshConversationHistory]);
    // Sync conversationId with URL
    useUrlSync(conversationState.conversationId);
    // Wrap onSignIn to pass the last user message so the sign-in handler
    // can embed it in the return URL for auto-resend after login.
    const wrappedOnSignIn = useCallback(() => {
        if (!conversationState.onSignIn)
            return;
        const lastUserMessage = [...conversationState.conversationHistory].reverse().find((m) => m.role === 'user');
        conversationState.onSignIn(lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.content);
    }, [conversationState]);
    // Auto-resend pending login message after user signs in and gets redirected back.
    // The `concierge_resend` sessionStorage key is set when the user clicks "Sign In"
    // on the login wall. It's tab-scoped and consumed immediately on mount.
    // The `isSignedIn` guard ensures it only fires if the user actually completed sign-in.
    const hasCheckedPendingLoginRef = useRef(false);
    useEffect(() => {
        if (hasCheckedPendingLoginRef.current)
            return;
        const pendingMessage = sessionStorage.getItem('concierge_resend');
        if (!pendingMessage) {
            hasCheckedPendingLoginRef.current = true;
            return;
        }
        // Only auto-resend if the user is now signed in
        if (!conversationState.isSignedIn)
            return;
        hasCheckedPendingLoginRef.current = true;
        // Remove immediately so it doesn't fire again
        sessionStorage.removeItem('concierge_resend');
        // Clear old conversation so the loginRequired message isn't shown
        conversationState.setConversationHistory([]);
        conversationState.setConversationId(null);
        conversationState.clearStreamingState();
        // Auto-send after a short delay to let the UI initialize
        setTimeout(() => performSearch(pendingMessage), 500);
    }, [performSearch, conversationState]);
    // Auto-send from URL params
    useAutoSendFromUrl({
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationLength: conversationState.conversationHistory.length,
        onSearch: handleSearch,
    });
    const showWelcome = conversationState.conversationHistory.length === 0 && !isLoadingConversationQuery;
    // Shared chat area props
    const chatAreaProps = Object.assign(Object.assign({ appName,
        brandId, conversationId: conversationState.conversationId, apiBaseUrl: conversationState.apiBaseUrl, isEmbedded: effectiveIsEmbedded, userFirstName, conversationHistory: conversationState.conversationHistory, productMetadataMap: conversationState.productMetadataMap, surveyMetadataMap: conversationState.surveyMetadataMap, streamingProductGroups: conversationState.streamingProductGroups, previewSearchQueries: conversationState.previewSearchQueries, previewQueryProducts: conversationState.previewQueryProducts, followUpQuestions: conversationState.followUpQuestions, streamingFeaturedProduct: conversationState.streamingFeaturedProduct, streamingStoreLocator: conversationState.streamingStoreLocator, streamingLoginRequired: conversationState.streamingLoginRequired, onSignIn: wrappedOnSignIn, isActivelyStreaming: conversationState.isActivelyStreaming, streamingAIResponse: conversationState.streamingAIResponse, currentStatus: conversationState.currentStatus, isWaitingForFollowUpQueries: conversationState.isWaitingForFollowUpQueries, isLoadingConversationQuery,
        isPendingAssistantResponse,
        isPollingTimedOut, onRetry: handleRetry, handleSearch, onUpdateFeedback: handleUpdateFeedback, onVariantSelect, onStop: cancelStream, searchInputValue,
        setSearchInputValue }, (layout === 'sidebar' && {
        onHomeClick,
        isSidebarExpanded,
        brandLogoUrl: appLogoUrl,
    })), { 
        // Sharing state
        isSharedView: conversationState.isSharedView, sharedByUserName: conversationState.sharedByUserName, sourceConversationId: conversationState.sourceConversationId, sourceCheckpointIndex: conversationState.sourceCheckpointIndex });
    const devOptionsPanel = jsx(DevOptionsPanel, Object.assign({}, devOpts));
    // Checkout drawer (shared between layouts)
    const checkoutDrawer = CheckoutDrawerContent && (jsx(Drawer, Object.assign({ id: "cart-popover", open: isCheckoutDrawerOpen, sx: {
            zIndex: 1300,
            width: 462,
            maxWidth: '100%',
        }, PaperProps: {
            sx: {
                boxShadow: 'none',
                borderRadius: '0',
                maxWidth: '100%',
            },
        }, anchor: "right", disableAutoFocus: true, disableEnforceFocus: true, disableRestoreFocus: true }, { children: jsx(CheckoutDrawerContent, {}) })));
    // Sidebar layout - row direction with sidebar on left
    if (layout === 'sidebar') {
        return (jsxs(Fragment, { children: [jsx(Toaster, {}), jsxs("div", Object.assign({ className: "concierge-root", style: {
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100dvh',
                        minHeight: '-webkit-fill-available',
                    } }, { children: [jsx(ConciergeSidebar, { onConversationSelect: handleConversationSelect, onComposeClick: handleComposeClick, currentConversationId: conversationState.conversationId, userProfile: userProfile, apiBaseUrl: conversationState.apiBaseUrl, onSignOutClick: onSignOutClick, onHomeClick: onHomeClick, brandLogoUrl: appLogoUrl, isExpanded: isSidebarExpanded, onToggle: () => setIsSidebarExpanded((prev) => !prev) }), jsxs("div", Object.assign({ className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: { minWidth: 0 } }, { children: [devOptionsPanel, jsx(ConciergeChatAreaV2, Object.assign({}, chatAreaProps))] }))] })), checkoutDrawer] }));
    }
    // Header layout (default) - column direction with header on top
    return (jsxs(Fragment, { children: [jsx(Toaster, {}), jsxs("div", Object.assign({ className: "concierge-root", style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100dvh',
                    minHeight: '-webkit-fill-available',
                } }, { children: [jsx(ConciergeHeader, { onConversationSelect: handleConversationSelect, onComposeClick: handleComposeClick, showComposeIcon: !showWelcome, cartCount: cartCount, onBagClick: onBagClick }), devOptionsPanel, jsx(ConciergeChatAreaV2, Object.assign({}, chatAreaProps))] })), checkoutDrawer] }));
};

export { ConciergeContainer as default };
//# sourceMappingURL=ConciergeContainer.js.map
